import { useState, useEffect, useCallback } from "react";
import axios, { getApiUrl } from "@/lib/api";
import { Shield, Loader2, Check, X, UserCheck, ExternalLink, MessageSquare, ShieldX, Flag } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const API = `${getApiUrl()}/api`;

const REASON_LABELS = {
  profanity: "Profanity",
  harassment: "Harassment",
  nsfw: "NSFW",
  broken_rule: "Broke Kid-Friendly rules",
  scam: "Scam / fraud",
  other: "Other",
};

function MultiplayerReports() {
  const [reports, setReports] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [reviewFilter, setReviewFilter] = useState("pending");
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/multiplayer/admin/reports`, {
        params: { admin_review: reviewFilter, status_filter: "all" },
      });
      setReports(r.data.reports || []);
      setSummary(r.data.user_summary || {});
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load reports");
    } finally {
      setLoading(false);
    }
  }, [reviewFilter]);

  useEffect(() => { load(); }, [load]);

  const handleApprove = async (markId) => {
    setBusyId(markId);
    try {
      await axios.post(`${API}/multiplayer/admin/reports/${markId}/approve`);
      toast.success("Report approved (mark kept)");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally {
      setBusyId(null);
    }
  };

  const handleDismiss = async (markId) => {
    setBusyId(markId);
    try {
      await axios.post(`${API}/multiplayer/admin/reports/${markId}/dismiss`);
      toast.success("Report dismissed");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally {
      setBusyId(null);
    }
  };

  const handleUnblock = async (userId) => {
    setBusyId(userId);
    try {
      await axios.post(`${API}/multiplayer/admin/users/${userId}/unblock`);
      toast.success("User unblocked");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Select value={reviewFilter} onValueChange={setReviewFilter}>
          <SelectTrigger className="w-[200px] bg-white/5 border-white/10 text-white" data-testid="sa-mod-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="pending">Pending review</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="dismissed">Dismissed</SelectItem>
            <SelectItem value="all">All</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-white/40 text-xs">{reports.length} report{reports.length !== 1 ? "s" : ""}</span>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-white/30" /></div>
      ) : reports.length === 0 ? (
        <Card className="bg-[#0a0a0a] border-white/5">
          <CardContent className="py-12 text-center text-white/40">
            No reports in this filter.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {reports.map((m) => {
            const s = summary[m.reported_user_id] || { active: 0, blocked: false };
            return (
              <Card key={m.id} className="bg-[#0a0a0a] border-white/5" data-testid={`sa-mod-report-${m.id}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <CardTitle className="text-white text-base flex items-center gap-2 flex-wrap">
                        <span className="text-red-300">{REASON_LABELS[m.reason] || m.reason}</span>
                        <span className="text-white/40 text-xs font-normal">·</span>
                        <span className="text-white/70 text-sm font-normal">{m.reported_user_name}</span>
                        {s.blocked && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/15 text-red-300 border border-red-500/30">BLOCKED</span>
                        )}
                        <span className={`text-[10px] px-1.5 py-0.5 rounded border ${
                          s.active >= 5 ? "bg-red-500/15 text-red-300 border-red-500/30" :
                          s.active >= 3 ? "bg-amber-500/15 text-amber-300 border-amber-500/30" :
                          "bg-white/5 text-white/50 border-white/10"
                        }`}>{s.active} active marks</span>
                      </CardTitle>
                      <p className="text-white/40 text-xs mt-0.5">
                        Reported by <span className="text-white/60">{m.reporter_user_name}</span> · {new Date(m.created_at).toLocaleString()} · expires {new Date(m.expires_at).toLocaleDateString()}
                      </p>
                    </div>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded border shrink-0 ${
                      m.admin_review === "approved" ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" :
                      m.admin_review === "dismissed" ? "bg-white/5 text-white/40 border-white/10" :
                      "bg-amber-500/15 text-amber-300 border-amber-500/30"
                    }`}>{m.admin_review}</span>
                  </div>
                </CardHeader>
                <CardContent className="pt-0 space-y-3">
                  <p className="text-white/80 text-sm whitespace-pre-wrap bg-white/[0.03] rounded p-2">{m.details}</p>
                  {m.screenshot_url && (
                    <a href={m.screenshot_url} target="_blank" rel="noreferrer">
                      <img src={m.screenshot_url} alt="screenshot" className="max-h-48 rounded border border-white/10 hover:border-white/30" />
                    </a>
                  )}
                  <div className="flex items-center gap-2 pt-1 flex-wrap">
                    {m.admin_review === "pending" && (
                      <>
                        <Button
                          size="sm"
                          data-testid={`sa-mod-approve-${m.id}`}
                          onClick={() => handleApprove(m.id)}
                          disabled={busyId === m.id}
                          className="bg-emerald-600/80 hover:bg-emerald-600"
                        >
                          {busyId === m.id ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Check className="w-3.5 h-3.5 mr-1.5" />}
                          Approve (keep mark)
                        </Button>
                        <Button
                          size="sm"
                          data-testid={`sa-mod-dismiss-${m.id}`}
                          onClick={() => handleDismiss(m.id)}
                          disabled={busyId === m.id}
                          variant="outline"
                          className="bg-white/5 border-white/10 text-white/70 hover:bg-white/10"
                        >
                          <X className="w-3.5 h-3.5 mr-1.5" /> Dismiss
                        </Button>
                      </>
                    )}
                    {m.admin_review === "approved" && m.status === "active" && (
                      <Button
                        size="sm"
                        onClick={() => handleDismiss(m.id)}
                        disabled={busyId === m.id}
                        variant="outline"
                        className="bg-white/5 border-white/10 text-white/70 hover:bg-white/10"
                      >
                        <X className="w-3.5 h-3.5 mr-1.5" /> Remove mark
                      </Button>
                    )}
                    {s.blocked && (
                      <Button
                        size="sm"
                        data-testid={`sa-mod-unblock-${m.reported_user_id}`}
                        onClick={() => handleUnblock(m.reported_user_id)}
                        disabled={busyId === m.reported_user_id}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        <UserCheck className="w-3.5 h-3.5 mr-1.5" /> Lift Multiplayer Ban
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

function formatTime(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString(); } catch { return ""; }
}

function CommentReports({ onCount }) {
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState("open");
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [removeReason, setRemoveReason] = useState("");
  const [removing, setRemoving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/community/admin/comment-reports`, {
        params: { status_filter: statusFilter },
      });
      setItems(r.data.items || []);
      onCount?.(r.data.openTotal || 0);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load comment reports");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, onCount]);

  useEffect(() => { load(); }, [load]);

  const handleDismiss = async (commentId) => {
    setBusyId(commentId);
    try {
      await axios.post(`${API}/community/admin/comment-reports/${commentId}/dismiss`);
      toast.success("Reports dismissed — comment kept");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally {
      setBusyId(null);
    }
  };

  const confirmRemove = async () => {
    if (!removeTarget) return;
    setRemoving(true);
    try {
      await axios.post(`${API}/community/comments/${removeTarget.commentId}/admin-delete`, {
        reason: removeReason.trim(),
      });
      toast.success("Comment removed and reports resolved");
      setRemoveTarget(null);
      setRemoveReason("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to remove comment");
    } finally {
      setRemoving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[200px] bg-white/5 border-white/10 text-white" data-testid="sa-comment-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="open">Open reports</SelectItem>
            <SelectItem value="resolved">Resolved</SelectItem>
            <SelectItem value="all">All</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-white/40 text-xs">{items.length} comment{items.length !== 1 ? "s" : ""}</span>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-white/30" /></div>
      ) : items.length === 0 ? (
        <Card className="bg-[#0a0a0a] border-white/5">
          <CardContent className="py-12 text-center text-white/40">No reported comments in this filter.</CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {items.map((it) => (
            <Card key={it.commentId} className="bg-[#0a0a0a] border-white/5" data-testid={`sa-comment-report-${it.commentId}`}>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <CardTitle className="text-white text-base flex items-center gap-2 flex-wrap">
                      <span className="text-white/80 text-sm">{it.authorName}</span>
                      {it.openCount > 0 && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-orange-500/15 text-orange-300 border border-orange-500/30 flex items-center gap-1">
                          <Flag className="w-2.5 h-2.5" />{it.openCount} open
                        </span>
                      )}
                      {it.deletedByAdmin && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/15 text-red-300 border border-red-500/30">REMOVED</span>
                      )}
                    </CardTitle>
                    <p className="text-white/40 text-xs mt-0.5">
                      On news: <Link to="/social" className="text-blue-400 hover:underline">{it.updateTitle}</Link>
                    </p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-0 space-y-3">
                {it.deletedByAdmin ? (
                  <p className="text-sm text-red-400/70 italic bg-white/[0.03] rounded p-2">
                    post deleted by admin per "{it.deletedReason}" rule
                  </p>
                ) : (
                  <p className="text-white/80 text-sm whitespace-pre-wrap break-words bg-white/[0.03] rounded p-2">{it.content}</p>
                )}

                <div className="space-y-1.5">
                  {it.reports.map((r) => (
                    <div key={r.id} className="text-xs flex items-start gap-2 bg-white/[0.02] rounded px-2 py-1.5">
                      <Flag className={`w-3 h-3 mt-0.5 shrink-0 ${r.status === "open" ? "text-orange-400" : "text-white/30"}`} />
                      <div className="min-w-0">
                        <span className="text-white/70 font-medium">{r.reporterName}</span>
                        <span className="text-white/40"> · {formatTime(r.createdAt)}</span>
                        {r.status !== "open" && <span className="text-white/30 italic"> · {r.status}</span>}
                        <p className="text-white/60">{r.reason}</p>
                      </div>
                    </div>
                  ))}
                </div>

                {!it.deletedByAdmin && it.openCount > 0 && (
                  <div className="flex items-center gap-2 pt-1 flex-wrap">
                    <Button
                      size="sm"
                      data-testid={`sa-comment-remove-${it.commentId}`}
                      onClick={() => { setRemoveTarget(it); setRemoveReason(""); }}
                      className="bg-amber-600 hover:bg-amber-700"
                    >
                      <ShieldX className="w-3.5 h-3.5 mr-1.5" /> Remove comment
                    </Button>
                    <Button
                      size="sm"
                      data-testid={`sa-comment-dismiss-${it.commentId}`}
                      onClick={() => handleDismiss(it.commentId)}
                      disabled={busyId === it.commentId}
                      variant="outline"
                      className="bg-white/5 border-white/10 text-white/70 hover:bg-white/10"
                    >
                      {busyId === it.commentId ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <X className="w-3.5 h-3.5 mr-1.5" />}
                      Dismiss reports
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!removeTarget} onOpenChange={(v) => { if (!v) setRemoveTarget(null); }}>
        <DialogContent className="max-w-md bg-[#0A0A0A] border-white/10 text-white" data-testid="sa-comment-remove-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldX className="w-5 h-5 text-amber-400" /> Remove Comment
            </DialogTitle>
            <DialogDescription className="text-white/40 text-xs">
              The comment stays in place showing: <em>post deleted by admin per "&lt;rule&gt;" rule</em>. Open reports will be resolved.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2 space-y-1.5">
            <label className="text-xs text-white/60">Rule / reason</label>
            <Input value={removeReason} onChange={(e) => setRemoveReason(e.target.value)}
              placeholder="e.g., No spam, Be respectful" className="bg-black/50 border-white/10"
              data-testid="sa-comment-remove-reason" />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRemoveTarget(null)}>Cancel</Button>
            <Button onClick={confirmRemove} disabled={removing}
              className="bg-amber-600 hover:bg-amber-700" data-testid="sa-comment-remove-confirm">
              {removing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Remove Comment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function SuperAdminModeration() {
  const [tab, setTab] = useState("multiplayer");
  const [commentOpen, setCommentOpen] = useState(0);

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white font-[Unbounded] flex items-center gap-2">
            <Shield className="w-6 h-6 text-red-400" />
            Reports
          </h1>
          <p className="text-white/40 text-sm mt-1">Review multiplayer moderation marks and reported community comments.</p>
        </div>
        <Link to="/super-admin" className="text-blue-400 text-sm hover:underline flex items-center gap-1">
          <ExternalLink className="w-3.5 h-3.5" /> Back to Super Admin
        </Link>
      </div>

      <div className="flex items-center gap-2 border-b border-white/10">
        <button
          onClick={() => setTab("multiplayer")}
          data-testid="sa-tab-multiplayer"
          className={`flex items-center gap-2 px-3 py-2 text-sm border-b-2 -mb-px transition-colors ${
            tab === "multiplayer" ? "border-red-400 text-white" : "border-transparent text-white/40 hover:text-white/70"
          }`}
        >
          <Shield className="w-4 h-4" /> Parental / Multiplayer
        </button>
        <button
          onClick={() => setTab("comments")}
          data-testid="sa-tab-comments"
          className={`flex items-center gap-2 px-3 py-2 text-sm border-b-2 -mb-px transition-colors ${
            tab === "comments" ? "border-orange-400 text-white" : "border-transparent text-white/40 hover:text-white/70"
          }`}
        >
          <MessageSquare className="w-4 h-4" /> Comments
          {commentOpen > 0 && (
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-orange-500/20 text-orange-300">{commentOpen}</span>
          )}
        </button>
      </div>

      {tab === "multiplayer" ? <MultiplayerReports /> : <CommentReports onCount={setCommentOpen} />}
    </div>
  );
}
